import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConnectionProvider } from "@/contexts/ConnectionContext";
import { ConnectionStatusBar } from "@/components/ConnectionStatusBar";
import { Toaster } from "@/components/ui/sonner";
import * as Sentry from "@sentry/react";

// Pages
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import AcceptInvitationPage from "@/pages/AcceptInvitationPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import ServiceRequestsPage from "@/pages/ServiceRequestsPage";
import AddServiceRequestPage from "@/pages/AddServiceRequestPage";
import ServiceRequestDetailPage from "@/pages/ServiceRequestDetailPage";
import FacilityManagementPage from "@/pages/FacilityManagementPage";
import FacilityDetailPage from "@/pages/FacilityDetailPage";
import UserManagementPage from "@/pages/UserManagementPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import WorkSchedulesPage from "@/pages/WorkSchedulesPage";
import VisualSchedulePage from "@/pages/VisualSchedulePage";
import AddWorkSchedulePage from "@/pages/AddWorkSchedulePage";
import WorkScheduleDetailPage from "@/pages/WorkScheduleDetailPage";
import DedicatedSiteScheduleDetailPage from "@/pages/DedicatedSiteScheduleDetailPage";
import EditWorkSchedulePage from "@/pages/EditWorkSchedulePage";
import VehiclesPage from "@/pages/VehiclesPage";
import CarFilePage from "@/pages/CarFilePage";
import LearningManagementPage from "@/pages/LearningManagementPage";
import MethodDetailPage from "@/pages/MethodDetailPage";
import NdtHoursTargetsPage from "@/pages/NdtHoursTargetsPage";
import WelderMaintenancePage from "@/pages/WelderMaintenancePage";
import WeldingPersonnelPage from "@/pages/WeldingPersonnelPage";
import WeldingPersonnelDetailPage from "@/pages/WeldingPersonnelDetailPage";
import MTEquipmentPage from "@/pages/MTEquipmentPage";
import BillingPage from "@/pages/BillingPage";
import BillingRequiredPage from "@/pages/BillingRequiredPage";
import {
  InventoryPage,
  TrainingDashboardPage,
  TrainingHSEPage,
  TrainingJobRolesPage,
  TrainingFunctionSpecificPage,
  SafetyCouncilPage,
  M1003B1sPage,
  M1003AuditsPage,
  M1003NCRsPage,
  M1003MDLPage,
  M1003ApprovedVendorsPage,
  DocumentLibraryPage,
} from "@/pages/PlaceholderPage";
import CustomersContactsPage from "@/pages/CustomersContactsPage";
import MainLayout from "@/components/layout/MainLayout";

// Role constants for ProtectedRoute - prevents re-creating arrays on every render
const ROLES_ADMIN_MANAGER = ['ADMIN', 'MANAGER'];
const ROLES_STAFF = ['ADMIN', 'MANAGER', 'OPERATOR'];
const ROLES_ALL = ['ADMIN', 'MANAGER', 'OPERATOR', 'TECHNICIAN'];

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null, bypassBilling = false }) => {
  const { user, loading, isBillingLocked } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Billing paywall: redirect locked users to /billing-required
  // unless bypassBilling is true (for billing-related pages)
  if (isBillingLocked && !bypassBilling && location.pathname !== '/billing-required') {
    return <Navigate to="/billing-required" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const defaultPath = user.role === 'TECHNICIAN' ? '/work-schedules' : '/dashboard';
    return <Navigate to={defaultPath} replace />;
  }

  return children;
};

// Public Route Component (redirects to intended destination or dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div>
      </div>
    );
  }

  if (user) {
    // Redirect to the originally requested page or dashboard
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      
      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />

      {/* Billing Required - accessible even when locked, standalone page (no sidebar) */}
      <Route path="/billing-required" element={
        <ProtectedRoute bypassBilling={true}>
          <BillingRequiredPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute bypassBilling={true}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="service-requests" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <ServiceRequestsPage />
          </ProtectedRoute>
        } />
        <Route path="service-requests/add" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <AddServiceRequestPage />
          </ProtectedRoute>
        } />
        <Route path="service-requests/:srId" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <ServiceRequestDetailPage />
          </ProtectedRoute>
        } />
        <Route path="facility-management" element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_MANAGER}>
            <FacilityManagementPage />
          </ProtectedRoute>
        } />
        <Route path="facility-management/:facilityId" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <FacilityDetailPage />
          </ProtectedRoute>
        } />
        <Route path="user-management" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <UserManagementPage />
          </ProtectedRoute>
        } />
        <Route path="work-schedules" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <WorkSchedulesPage />
          </ProtectedRoute>
        } />
        {/* Visual schedule lane view — Operators/Managers/Admins only. */}
        <Route path="work-schedules/visual" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <VisualSchedulePage />
          </ProtectedRoute>
        } />
        <Route path="work-schedules/add" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <AddWorkSchedulePage />
          </ProtectedRoute>
        } />
        <Route path="work-schedules/dedicated/:id" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <DedicatedSiteScheduleDetailPage />
          </ProtectedRoute>
        } />
        <Route path="work-schedules/:id" element={
          <ProtectedRoute allowedRoles={ROLES_ALL}>
            <WorkScheduleDetailPage />
          </ProtectedRoute>
        } />
        <Route path="work-schedules/:id/edit" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <EditWorkSchedulePage />
          </ProtectedRoute>
        } />
        <Route path="vehicles" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <VehiclesPage />
          </ProtectedRoute>
        } />
        <Route path="inventory" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <InventoryPage />
          </ProtectedRoute>
        } />
        <Route path="training-management" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <LearningManagementPage />
          </ProtectedRoute>
        } />
        <Route path="training/ndt" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <LearningManagementPage />
          </ProtectedRoute>
        } />
        <Route path="training/ndt/methods/:recordId" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <MethodDetailPage />
          </ProtectedRoute>
        } />
        <Route path="training/ndt/hours-targets" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <NdtHoursTargetsPage />
          </ProtectedRoute>
        } />
        <Route path="training/dashboard" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <TrainingDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="training/hse" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <TrainingHSEPage />
          </ProtectedRoute>
        } />
        <Route path="training/job-roles" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <TrainingJobRolesPage />
          </ProtectedRoute>
        } />
        <Route path="training/function-specific" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <TrainingFunctionSpecificPage />
          </ProtectedRoute>
        } />
        <Route path="training/welder" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            {/* Legacy landing — redirected per UAT 2026-05-29. The detail
                route /training/welder/:technician_id is still reachable from
                the new "View Maintenance Log" button. */}
            <Navigate to="/training/welding-personnel" replace />
          </ProtectedRoute>
        } />
        <Route path="training/welder/:technician_id" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <WelderMaintenancePage />
          </ProtectedRoute>
        } />
        <Route path="training/welding-personnel" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <WeldingPersonnelPage />
          </ProtectedRoute>
        } />
        <Route path="training/welding-personnel/:technician_id" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <WeldingPersonnelDetailPage />
          </ProtectedRoute>
        } />
        <Route path="training/safety-council" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <SafetyCouncilPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/mt-equipment" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <MTEquipmentPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/b1s" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <M1003B1sPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/audits" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <M1003AuditsPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/ncrs" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <M1003NCRsPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/mdl" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <M1003MDLPage />
          </ProtectedRoute>
        } />
        <Route path="m1003/approved-vendors" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <M1003ApprovedVendorsPage />
          </ProtectedRoute>
        } />
        <Route path="document-library" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <DocumentLibraryPage />
          </ProtectedRoute>
        } />
        <Route path="customers" element={
          <ProtectedRoute allowedRoles={ROLES_STAFF}>
            <CustomersContactsPage />
          </ProtectedRoute>
        } />
        <Route path="car-files/:car_mark/:car_number" element={
          <ProtectedRoute>
            <CarFilePage />
          </ProtectedRoute>
        } />
        <Route path="settings" element={<Navigate to="/billing" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Billing pages - inside MainLayout for sidebar, but bypass billing lock */}
        <Route path="billing" element={
          <ProtectedRoute bypassBilling={true} allowedRoles={ROLES_ADMIN_MANAGER}>
            <BillingPage />
          </ProtectedRoute>
        } />
        <Route path="billing/success" element={
          <ProtectedRoute bypassBilling={true} allowedRoles={ROLES_ADMIN_MANAGER}>
            <BillingPage />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Error Fallback Component
const ErrorFallback = ({ error, resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
      <p className="text-gray-600 mb-4">
        An unexpected error occurred. Our team has been notified.
      </p>
      <p className="text-sm text-gray-500 mb-6 font-mono bg-gray-100 p-2 rounded">
        {error?.message || 'Unknown error'}
      </p>
      <button
        onClick={resetError}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Try again
      </button>
    </div>
  </div>
);

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <ConnectionProvider>
        <AuthProvider>
          <BrowserRouter>
            <ConnectionStatusBar />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ConnectionProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
